<template>
    <b-modal
        lazy
        title-tag="h2"
        :title="details ? 'Edit Relationship' : 'New Relationship'"
        :visible="isOpen"
    >
        <b-alert :show="showAlert" variant="warning">
            <i class="cus-error"></i>
            This client is responsible for one or more active wellness plans,
            and therefore this relationship cannot be changed. Please remove the
            active wellness plan(s) before changing the relationship.
        </b-alert>

        <b-form-group label-cols="3" label="Patient:" label-for="patient">
            <span>{{ patientDetails ? patientDetails.name : "" }}</span>
        </b-form-group>

        <b-form-group label-cols="3" label="Type:" label-for="type">
            <b-form-select
                class="capitalize"
                :options="typeOptions"
                v-model="type"
                :disabled="isSaving"
                :class="{
                    'border-danger': formError.includes('type')
                }"
            />
        </b-form-group>

        <b-form-group label-cols="3" label="Name:" label-for="name">
            <!-- If type not selected yet -->
            <b-form-input
                v-if="!type"
                placeholder="Name (required)"
                :disabled="isSaving"
                v-model="inputNameNoType"
                :class="{
                    'border-danger': formError.includes('inputNameNoType')
                }"
            ></b-form-input>

            <!-- client only -->
            <vet-select
                v-if="showMenuClient"
                module="client"
                placeholder="Name (required)"
                v-model="selectedEntity"
                hideBackdrop
                :disabled="isSaving"
                :class="{
                    'border-danger': formError.includes('selectedEntity')
                }"
            />

            <!-- contact-actor and company only -->
            <vet-select
                v-if="showMenuContactCompany"
                module="contacts"
                placeholder="Name (required)"
                v-model="selectedEntity"
                :disabled="isSaving"
                :class="{
                    'border-danger': formError.includes('selectedEntity')
                }"
            />

            <!-- all -->
            <vet-select
                v-if="showMenuAll"
                module="client-all-contacts"
                placeholder="Name (required)"
                v-model="selectedEntity"
                :disabled="isSaving"
                :class="{
                    'border-danger': formError.includes('selectedEntity')
                }"
            />
        </b-form-group>

        <template v-slot:modal-footer>
            <loader v-if="isSaving" :margin="1" />

            <b-button
                class="mr-auto"
                variant="secondary"
                @click="close"
                v-if="!isSaving"
            >
                Cancel
            </b-button>

            <b-button-group v-if="!isSaving && !details">
                <b-dropdown
                    v-if="!continues"
                    @click="saveDetails(false)"
                    left
                    split
                    text="Save + Done"
                    variant="primary"
                >
                    <b-dropdown-item @click="saveDetails(true)"
                        >Save + Add New</b-dropdown-item
                    >
                </b-dropdown>
                <b-dropdown
                    v-else
                    @click="saveDetails(true)"
                    left
                    split
                    text="Save + Add New"
                    variant="primary"
                >
                    <b-dropdown-item @click="saveDetails(false)"
                        >Save + Done</b-dropdown-item
                    >
                </b-dropdown>
            </b-button-group>
            <b-button
                v-else-if="!isSaving && details"
                variant="primary"
                @click="saveDetails"
            >
                Save
            </b-button>
        </template>
    </b-modal>
</template>

<script>
import { find, has, forEach } from "lodash";
import Loader from "@/module/commons/loader";
import VetSelect from "@/module/commons/vet-select";
import Relationships from "@/services/patient-relationships.js";
import Client from "@/services/client";
import EVENT from "@/constants/events";

export default {
    name: "modalPatientRelatiohsipSave",
    components: {
        VetSelect,
        Loader
    },
    props: {
        patientDetails: {
            type: Object
        }
    },
    data() {
        return {
            isOpen: false,
            isSaving: false,
            patient: null,
            clientName: null,
            type: null,
            oldType: null,
            oldClient: [],
            typeOptions: [],
            rawOptions: [],
            details: null,
            showMenu: false,
            showMenuClient: false,
            showMenuContactCompany: false,
            showMenuAll: false,
            selectedEntity: null,
            isWellnessPlan: false,
            showAlert: false,
            formError: [],
            inputNameNoType: null,
            continues: false
        };
    },
    watch: {
        type(value, oldValue) {
            let self = this;
            this.formError = [];
            self.showMenuClient = false;
            self.showMenuContactCompany = false;
            self.showMenuAll = false;

            forEach(this.rawOptions, opt => {
                if (opt.id === value) {
                    self.showNameOption = opt.modules.replace(" ", "");

                    switch (opt.id) {
                        case 7:
                        case 29:
                            self.showMenuClient = true;
                            break;
                        case 8:
                        case 24:
                        case 25:
                        case 26:
                            self.showMenuAll = true;
                            break;
                        default:
                            self.showMenuContactCompany = true;
                            break;
                    }

                    let modules = opt.modules.replace(" ", "");

                    if (self.selectedEntity) {
                        if (modules == "1:2") {
                            if (
                                self.selectedEntity.type == 1 ||
                                self.selectedEntity.label.includes("(contact)")
                            ) {
                                self.selectedEntity = null;
                            }
                        } else if (
                            modules == "1:4" ||
                            modules == "1:3,1:4" ||
                            modules == "1:4,1:3"
                        ) {
                            // company and contact always has (contact)
                            if (
                                !self.selectedEntity.label.includes("(contact)")
                            ) {
                                self.selectedEntity = null;
                            }
                        }
                    }
                }
            });

            if (value !== oldValue && oldValue !== null) {
                self.inputNameNoType = null;
                self.selectedEntity = null;
            }
        },
        selectedEntity(value) {
            if (value) {
                let index = this.formError.indexOf("selectedEntity");
                if (index !== -1) {
                    this.formError.splice(index, 1);
                }
            }
        }
    },
    methods: {
        async open(relationship) {
            this.typeOptions = await this.generateRelationshipTypes();

            if (relationship) {
                this.type = relationship.relation_type.id;
                this.oldType = this.type;
                this.details = this.processOldDetails(relationship);
            }

            this.isOpen = true;
        },
        processOldDetails(relationship) {
            let data = _.clone(relationship);
            let object = {
                id: null,
                type: "actor",
                relation: data.relation_type.id
            };

            if (has(data, "business")) {
                object.type = "business";
                object.id = data.business_id;

                this.selectedEntity = {
                    id: data.business_id,
                    label: data.business.name + " (contact)",
                    type: 1
                };
            } else {
                object.id = data.actor_id;
                let moduleLabel = _.find(data.actor.module, [
                    "module_type",
                    "contact"
                ])
                    ? " (contact)"
                    : " (client)";

                this.selectedEntity = {
                    id: data.actor_id,
                    label: data.actor.label + moduleLabel,
                    type: 0
                };
                this.oldClient = this.selectedEntity;
            }

            if (this.type == 7) {
                this.getClientWellnessPlan(object.id, data.patient_id);
            }

            return object;
        },
        async getClientWellnessPlan(clientId, patientId) {
            let self = this;
            let wellnessPlan = await Client.getWellnessPlanByPatient(
                clientId,
                patientId
            );

            if (wellnessPlan.resource.length) {
                let activePlan = find(wellnessPlan.resource, ["status", 0]);
                if (activePlan) {
                    self.isWellnessPlan = true;
                }
            }
        },
        isShowContactsOnly() {
            if (this.showNameOption) {
                let trimedText = this.showNameOption.replace(" ", "");
                return trimedText == "1:3,1:4" || trimedText == "1:4,1:3";
            }

            return false;
        },
        close() {
            this.isOpen = false;
            this.isSaving = false;
        },
        async generateRelationshipTypes() {
            const rawTypes = await Relationships.getPatientRelationshipType();
            let result = [];

            if (rawTypes.message.code === 200) {
                this.rawOptions = rawTypes.resources;

                for (const type of rawTypes.resources) {
                    result.push({
                        text: _.startCase(type.value),
                        value: type.id
                    });
                }
            }

            result = result.sort((a, b) => (a.text > b.text ? 1 : -1));
            result.unshift({ text: "Choose Relationship Type", value: null });
            return result;
        },
        saveDetails(addNew) {
            this.formError = [];
            this.continues = addNew;
            if (this.type == null) {
                this.inputNameNoType = null;
                this.formError.push("type");
                this.formError.push("inputNameNoType");
            }

            if (
                this.selectedEntity == null ||
                !_.isObject(this.selectedEntity)
            ) {
                this.formError.push("selectedEntity");
                this.formError.push("inputNameNoType");
            }

            if (this.formError.length > 0) {
                return false;
            }

            if (this.details) {
                this.saveEdit();
            } else {
                this.saveNew(addNew);
            }
        },
        saveNew(addNew) {
            this.isSaving = true;

            let params = {
                entity_id: this.selectedEntity.id,
                entity_type:
                    this.selectedEntity.type == 1 ? "business" : "actor",
                relation_type: this.type
            };

            Relationships.addRelationship(this.patientDetails.id, params)
                .then(response => {
                    if (response.message.code === 200) {
                        this._message("Relationship updated");
                        this.$emit("reload");
                        this.$tabEvent.emit(EVENT.REFRESH_PATIENT_PROFILE);
                        addNew ? this.resetData() : this.close();
                    } else {
                        this._message(
                            "Error : Unable to edit relationship",
                            "danger"
                        );
                        addNew ? this.resetData() : this.close();
                    }
                })
                .catch(error => {
                    this._message(
                        "Error : Unable to edit relationship",
                        "danger"
                    );
                    addNew ? this.resetData() : this.close();
                    console.error(error);
                });
        },
        saveEdit() {
            this.isSaving = true;

            if (this.isWellnessPlan) {
                if (
                    (this.oldType == 7 &&
                        !_.isEqual(
                            this.selectedEntity.id,
                            this.oldClient.id
                        )) ||
                    (this.oldType == 7 && this.type != 7)
                ) {
                    this.showAlert = true;
                    this.isSaving = false;
                    return;
                } else {
                    this.showAlert = false;
                }
            }

            let params = {
                entity_id: this.selectedEntity.id,
                entity_type:
                    this.selectedEntity.type == 1 ? "business" : "actor",
                relation_type: this.type,
                old_entity_id: this.details.id,
                old_entity_type: this.details.type,
                old_relation_type: this.details.relation
            };

            Relationships.updateRelationship(this.patientDetails.id, params)
                .then(response => {
                    if (response.message.code) {
                        this._message("Relationship updated");
                        this.$emit("reload");
                        this.$tabEvent.emit(EVENT.REFRESH_PATIENT_PROFILE);
                        this.close();
                    } else {
                        this._message(
                            "Error : Unable to edit relationship",
                            "danger"
                        );
                        this.close();
                    }
                })
                .catch(error => {
                    this._message(
                        "Error : Unable to edit relationship",
                        "danger"
                    );
                    this.close();
                    console.error(error);
                });
        },
        resetData() {
            this.isSaving = false;
            this.type = null;
            this.selectedEntity = null;
            this.inputNameNoType = null;
        }
    }
};
</script>

<style scoped>
.capitalize {
    text-transform: capitalize;
}
</style>
